import { state } from '../state';
import { get, post } from '../api';
import { showModal, closeModal, modalFrame } from '../components/modal';
import { showToast } from '../components/toast';
import { validateRequired, clearAllErrors } from '../components/form-validation';
import { iconPlus } from '../icons';

function esc(str: string): string {
  const el = document.createElement('div');
  el.textContent = str;
  return el.innerHTML;
}

interface ContractorTask {
  id: string;
  server_id: string;
  contractor_id: string;
  title: string;
  category: string;
  status: string;
  priority: number;
  description: string;
  due_date: string | null;
  assigned_to: string | null;
  completed_at: string | null;
  notes: string;
  created_at: string;
  updated_at: string;
}

type Filter = 'all' | 'pending' | 'in_progress' | 'completed';
let activeFilter: Filter = 'all';

function categoryBadge(cat: string): string {
  const colors: Record<string, string> = {
    paperwork: 'var(--logic)',
    phone_call: 'var(--info)',
    sourcing: 'var(--ok)',
    document: 'var(--warn)',
    general: 'var(--fg-muted)',
  };
  const color = colors[cat] || 'var(--fg-muted)';
  return `<span class="ct-badge" style="background:${color}20;color:${color}">${esc(cat.replace('_', ' '))}</span>`;
}

function priorityLabel(p: number): string {
  if (p >= 2) return '<span class="ct-priority ct-urgent">urgent</span>';
  if (p >= 1) return '<span class="ct-priority ct-high">high</span>';
  return '';
}

function statusDot(s: string): string {
  const cls = s === 'completed' ? 'ct-dot-green' : s === 'in_progress' ? 'ct-dot-yellow' : s === 'blocked' ? 'ct-dot-red' : 'ct-dot-grey';
  return `<span class="ct-dot ${cls}"></span>`;
}

function taskCard(task: ContractorTask, reload: () => void): HTMLElement {
  const card = document.createElement('div');
  card.className = 'ct-card';
  card.innerHTML = `
    <div class="ct-card-header">
      ${statusDot(task.status)}
      <span class="ct-card-title">${esc(task.title)}</span>
      ${priorityLabel(task.priority)}
      ${categoryBadge(task.category)}
    </div>
    ${task.description ? `<div class="ct-card-desc">${esc(task.description)}</div>` : ''}
    <div class="ct-card-meta">
      ${task.due_date ? `<span>Due: ${esc(task.due_date)}</span>` : ''}
      <span>${esc(task.status)}</span>
    </div>
    <div class="ct-card-actions">
      ${task.status !== 'completed' ? `<button class="btn btn-sm btn-secondary ct-complete-btn">Complete</button>` : ''}
      ${task.status === 'pending' ? `<button class="btn btn-sm btn-secondary ct-start-btn">Start</button>` : ''}
    </div>
  `;

  card.querySelector('.ct-complete-btn')?.addEventListener('click', () => {
    post('/api/contractor/tasks/update', { id: task.id, status: 'completed' })
      .then(() => { showToast('Task completed', 'ok'); reload(); })
      .catch(e => showToast(String(e), 'err'));
  });

  card.querySelector('.ct-start-btn')?.addEventListener('click', () => {
    post('/api/contractor/tasks/update', { id: task.id, status: 'in_progress' })
      .then(() => { showToast('Task started', 'ok'); reload(); })
      .catch(e => showToast(String(e), 'err'));
  });

  return card;
}

function openAddTask(reload: () => void): void {
  const people = state.people || [];
  const contractorOptions = people
    .filter(p => p.role === 'contractor' || p.status === 'onboarding')
    .map(p => `<option value="${esc(p.id)}">${esc(p.name)}</option>`)
    .join('');

  showModal(modalFrame('Add Contractor Task',
    `<div class="form-group">
      <label class="form-label" for="f-title">Task Title</label>
      <input id="f-title" class="form-input" required placeholder="What needs to be done?">
    </div>
    <div class="form-group">
      <label class="form-label" for="f-contractor">Contractor</label>
      <select id="f-contractor" class="form-input">
        <option value="">Select contractor...</option>
        ${contractorOptions}
      </select>
    </div>
    <div class="form-row">
      <div class="form-group">
        <label class="form-label" for="f-category">Category</label>
        <select id="f-category" class="form-input">
          <option value="general">General</option>
          <option value="paperwork">Paperwork</option>
          <option value="phone_call">Phone Call</option>
          <option value="sourcing">Sourcing</option>
          <option value="document">Document</option>
        </select>
      </div>
      <div class="form-group">
        <label class="form-label" for="f-priority">Priority</label>
        <select id="f-priority" class="form-input">
          <option value="0">Normal</option>
          <option value="1">High</option>
          <option value="2">Urgent</option>
        </select>
      </div>
    </div>
    <div class="form-group">
      <label class="form-label" for="f-desc">Description</label>
      <textarea id="f-desc" class="form-input" rows="3" placeholder="Details, links, instructions..."></textarea>
    </div>
    <div class="form-group">
      <label class="form-label" for="f-due">Due Date</label>
      <input id="f-due" class="form-input" type="date">
    </div>`,
    `<button class="btn btn-secondary" id="modal-cancel">Cancel</button>
     <button class="btn btn-primary" id="modal-save">Save Task</button>`
  ));

  document.getElementById('modal-close')?.addEventListener('click', closeModal);
  document.getElementById('modal-cancel')?.addEventListener('click', closeModal);
  document.getElementById('modal-save')?.addEventListener('click', () => {
    clearAllErrors();
    if (!validateRequired('f-title', 'Title')) return;

    const title = (document.getElementById('f-title') as HTMLInputElement).value.trim();
    const contractor_id = (document.getElementById('f-contractor') as HTMLSelectElement).value || state.ownerId || '';
    const category = (document.getElementById('f-category') as HTMLSelectElement).value;
    const priority = parseInt((document.getElementById('f-priority') as HTMLSelectElement).value, 10);
    const description = (document.getElementById('f-desc') as HTMLTextAreaElement).value.trim();
    const due_date = (document.getElementById('f-due') as HTMLInputElement).value || null;

    post('/api/contractor/tasks/add', {
      server_id: state.currentServer,
      contractor_id,
      title,
      category,
      priority,
      description,
      due_date,
    })
      .then(() => { closeModal(); showToast('Task added', 'ok'); reload(); })
      .catch(e => showToast(String(e), 'err'));
  });
}

export function renderContractor(body: HTMLElement): void {
  const sid = state.currentServer;
  const reload = (): void => renderContractor(body);

  const tabs = (['all', 'pending', 'in_progress', 'completed'] as const).map(f =>
    `<button class="btn btn-sm ${activeFilter === f ? 'btn-primary' : 'btn-secondary'}" data-filter="${f}">${esc(f === 'in_progress' ? 'In Progress' : f.charAt(0).toUpperCase() + f.slice(1))}</button>`
  ).join('');

  body.innerHTML = `
    <div class="channel-header">
      <h1>Contractor Onboarding</h1>
      <button class="btn btn-sm btn-primary" id="ct-add-btn">${iconPlus()} Add Task</button>
    </div>
    <div class="filter-row">${tabs}</div>
    <div id="ct-list" class="ct-list"><p class="loading-text">Loading tasks...</p></div>
  `;

  body.querySelectorAll<HTMLButtonElement>('[data-filter]').forEach(btn => {
    btn.addEventListener('click', () => {
      activeFilter = (btn.dataset['filter'] as Filter) || 'all';
      reload();
    });
  });

  document.getElementById('ct-add-btn')?.addEventListener('click', () => openAddTask(reload));

  // Fetch tasks
  const url = sid
    ? `/api/contractor/tasks?server_id=${encodeURIComponent(sid)}`
    : '/api/contractor/tasks';

  get<ContractorTask[]>(url)
    .then(tasks => {
      const filtered = activeFilter === 'all'
        ? tasks
        : tasks.filter(t => t.status === activeFilter);
      const listEl = document.getElementById('ct-list');
      if (!listEl) return;

      if (filtered.length === 0) {
        listEl.innerHTML = `<div class="empty-state"><p>No ${activeFilter === 'all' ? '' : activeFilter + ' '}tasks yet</p></div>`;
        return;
      }

      listEl.innerHTML = '';
      for (const task of filtered) {
        listEl.appendChild(taskCard(task, reload));
      }
    })
    .catch(() => {
      const listEl = document.getElementById('ct-list');
      if (listEl) listEl.innerHTML = '<p class="empty-state">Could not load tasks</p>';
    });
}
